const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04]

const readBlobHeader = (blob: Blob, length: number): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer))
    reader.onerror = () =>
      reject(reader.error ?? new Error('FileReader failed'))
    reader.readAsArrayBuffer(blob.slice(0, length))
  })

const looksLikeZip = async (blob: Blob) => {
  const header = await readBlobHeader(blob, ZIP_MAGIC.length)
  return ZIP_MAGIC.every((byte, index) => header[index] === byte)
}

const withZipExtension = (fileName: string) =>
  fileName.toLowerCase().endsWith('.zip') ? fileName : `${fileName}.zip`

/**
 * Some docserv-generated exports are served as a zip containing a single
 * file rather than the file itself (observed for large manifest exports).
 * Unwrap that case so the browser download matches the requested fileName's
 * extension instead of silently mislabeling a zip archive (e.g. as `.csv`).
 *
 * Returns the resulting blob/fileName pair to download: the inner file (kept
 * under the originally requested fileName) when the blob is a zip containing
 * exactly one file, the original blob renamed with a `.zip` extension when
 * it's a zip containing anything else, or the original blob/fileName
 * untouched when it isn't a zip at all (checked by magic number, not by
 * whether JSZip can parse it — a response that starts with the zip
 * signature but fails to parse is a corrupted/truncated download, which
 * should surface as an error rather than silently downloading under the
 * original fileName).
 */
const resolveDownload = async (
  blob: Blob,
  fileName: string
): Promise<{ blob: Blob; fileName: string }> => {
  if (!(await looksLikeZip(blob))) {
    return { blob, fileName }
  }
  const JSZip = (await import('jszip')).default
  const zip = await JSZip.loadAsync(blob)
  const entries = Object.values(zip.files).filter((entry) => !entry.dir)
  const [onlyEntry] = entries
  if (entries.length !== 1 || !onlyEntry) {
    return { blob, fileName: withZipExtension(fileName) }
  }
  return { blob: await onlyEntry.async('blob'), fileName }
}

/**
 * @usage ```
 * try {
 *   downloadFileFromUrl({
 *     fileName: 'downloaded_file.pdf',
 *     url: 'https://example.com/path/to/file.pdf',
 *   })
 * } catch (e) {
 *   showSnackbar(...)
 * }
 * ```
 */
export const downloadFileFromUrl = async ({
  fileName,
  url,
  unwrapZip = false,
}: {
  fileName: string
  url: string
  /**
   * Opt-in: some servers (docserv, for large manifest exports) zip their
   * response instead of serving the file directly. Set this when the caller
   * expects a single non-zip file so that case gets unwrapped/corrected
   * instead of silently saving the zip under the requested fileName. Leave
   * off for callers that may legitimately download a real `.zip`, or an
   * Office/archive format that also starts with the zip signature
   * (`.xlsx`, `.docx`, `.pptx`, `.jar`, …) — those would otherwise get
   * mis-detected as "a zip to unwrap".
   */
  unwrapZip?: boolean
}) => {
  /**
   * Fetch and create object URL.
   * If we link to the url directly, we cannot set the file name
   * and the browser might open a new tab for certain file types.
   */
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  const fetchedBlob = await response.blob()
  const { blob, fileName: downloadFileName } = unwrapZip
    ? await resolveDownload(fetchedBlob, fileName)
    : { blob: fetchedBlob, fileName }

  const objectUrl = URL.createObjectURL(blob)

  // Create a temporary anchor element
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = downloadFileName
  document.body.appendChild(a)

  // Trigger the download
  a.click()

  // Clean up
  document.body.removeChild(a)
  URL.revokeObjectURL(objectUrl) // Free up the memory
}
